import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCaretDown, FaSignOutAlt } from "react-icons/fa";
import { logout } from "../../utils/auth";
import { groupMenuItems } from "./menuItems";
import "./styles.css";

const isLinkActive = (pathname, link) => {
  if (!link) return false;
  if (pathname === link) return true;
  if (link !== "/" && pathname.startsWith(`${link}/`)) return true;
  return false;
};

const getUserMeta = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = String(user.name || user.username || "User").trim();
    const roleLabel =
      Number(user.role) === 0
        ? "Super Admin"
        : Number(user.role) === 1
        ? "Admin"
        : Number(user.role) === 4
        ? "Loan Account"
        : "Branch";
    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");
    return { name, roleLabel, initials: initials || "U" };
  } catch {
    return { name: "User", roleLabel: "Branch", initials: "U" };
  }
};

const SideMenuItem = ({
  item,
  index,
  currentPathname,
  openItem,
  toggleSubItems,
  handleNavigation,
}) => {
  const active =
    isLinkActive(currentPathname, item.link) ||
    item.subItems?.some((sub) => isLinkActive(currentPathname, sub.link));
  const hasSub = Boolean(item.subItems?.length);

  const handleClick = () => {
    if (hasSub) toggleSubItems(index);
    else if (item.link === "/logout" || item.title === "Logout") logout();
    else handleNavigation(item.link);
  };

  return (
    <div className="w-full">
      <button
        type="button"
        className={`side-menu-item ${active ? "side-menu-item-active" : ""}`}
        onClick={handleClick}
      >
        <span className="side-menu-item-icon">{item.icon}</span>
        <span className="flex-1 truncate">{item.title}</span>
        {hasSub && (
          <FaCaretDown
            className={`shrink-0 text-[10px] opacity-70 transition-transform duration-200 ${
              openItem === index ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {hasSub && openItem === index && (
        <div className="side-menu-sub-wrap">
          {item.subItems.map((subItem, subIndex) => {
            const subActive = isLinkActive(currentPathname, subItem.link);
            return (
              <button
                key={subIndex}
                type="button"
                className={`side-menu-sub-item ${
                  subActive ? "side-menu-sub-item-active" : ""
                }`}
                onClick={() => handleNavigation(subItem.link)}
              >
                {subItem.title}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SideMenu = ({ sideMenuItems = [] }) => {
  const [openItem, setOpenItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPathname = location.pathname;
  const userMeta = useMemo(() => getUserMeta(), []);

  const groupedItems = useMemo(
    () => groupMenuItems(sideMenuItems),
    [sideMenuItems]
  );

  useEffect(() => {
    const idx = sideMenuItems.findIndex(
      (item) =>
        item.subItems?.some((sub) => isLinkActive(currentPathname, sub.link)) ||
        isLinkActive(currentPathname, item.link)
    );
    if (idx >= 0 && sideMenuItems[idx]?.subItems) {
      setOpenItem(idx);
    }
  }, [currentPathname, sideMenuItems]);

  const toggleSubItems = (index) => {
    setOpenItem((prev) => (prev === index ? null : index));
  };

  const handleNavigation = (link) => {
    if (link) navigate(link);
  };

  let itemIndex = 0;

  return (
    <aside className="side-menu-shell main-wrapper max850:hidden shrink-0 font-quicksand">
      <div className="flex h-full flex-col">
        <div className="side-menu-brand">
          <img
            src="/logo.svg"
            alt="Irshad Carton Dealer"
            className="h-8 w-auto object-contain"
          />
          <span className="side-menu-brand-title">Irshad Carton Dealer</span>
        </div>

        <nav className="side-menu-nav menu-list flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
          {groupedItems.map((group) => (
            <div key={group.label}>
              <p className="side-menu-section-label">{group.label}</p>
              <div className="flex flex-col gap-0 px-1.5 pb-0.5">
                {group.items.map((item) => {
                  const currentIndex = itemIndex;
                  itemIndex += 1;
                  return (
                    <SideMenuItem
                      key={`${group.label}-${item.title}-${currentIndex}`}
                      item={item}
                      index={currentIndex}
                      currentPathname={currentPathname}
                      openItem={openItem}
                      toggleSubItems={toggleSubItems}
                      handleNavigation={handleNavigation}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="side-menu-footer">
          <div className="side-menu-user-card">
            <div className="side-menu-user-avatar">{userMeta.initials}</div>
            <div className="min-w-0">
              <p className="truncate text-xs font-bold text-white leading-tight">
                {userMeta.name}
              </p>
              <p className="truncate text-[10px] font-semibold text-white/50 leading-tight">
                {userMeta.roleLabel}
              </p>
            </div>
          </div>
          <button type="button" className="side-menu-logout" onClick={logout}>
            <FaSignOutAlt className="text-xs" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default SideMenu;
